import type { CaptionKaraokeHighlightEffect } from './caption-highlight-effect';

export interface CaptionKaraokeWordTiming {
  text: string;
  startMs?: number;
  endMs?: number;
}

export interface CaptionKaraokeHighlightWord extends CaptionKaraokeWordTiming {
  fillRatio: number;
  isActive: boolean;
}

export interface KaraokeHighlightTimingOptions {
  /**
   * 강제 정렬처럼 검증된 word end를 그대로 사용한다.
   * false이면 짧게 양자화된 raw STT word end를 다음 단어까지 보완하는 legacy 동작을 유지한다.
   */
  preserveWordEndTiming?: boolean;
}

type WordVisualRange = {
  startRatio: number;
  endRatio: number;
};

// STT 벤더(Soniox)가 단어 end 를 start+60ms 로 양자화해 주는 데이터가 있어, end 를 그대로
// 쓰면 하이라이트가 발화보다 앞서 지나간다. 표시용 유효 end 를 다음 단어 start 까지 연장하되
// 긴 무음까지 삼키지 않도록 상한을 둔다. 원본 end 가 이미 다음 단어 start 와 겹치면 다음
// 단어 start 로 clamp 하고, 그 외에는 축소하지 않는다. 캡션 렌더링 전용 파생값이며 원본
// word 데이터(SRT export·word-boundary 편집 등)에는 영향을 주지 않는다.
// BE 데이터 보정과 값 동기화 필요: ai-api/common/vendors/stt/models/soniox_client.py 의
// MAX_GAP_FILLED_WORD_MS.
const MAX_EXTENDED_WORD_DURATION_MS = 1200;
const LAST_WORD_MIN_DURATION_MS = 320;

export const withEffectiveWordEnds = <T extends CaptionKaraokeWordTiming>(
  words: T[],
): T[] =>
  words.map((word, index) => {
    if (word.startMs == null || word.endMs == null) return word;
    const cappedEnd = word.startMs + MAX_EXTENDED_WORD_DURATION_MS;
    const nextStart = findNextTimedStart(words, index);
    // 비단조 데이터(다음 단어 start ≤ 현재 start)는 건드리지 않는다 (BE 구현과 동일).
    if (nextStart != null && nextStart <= word.startMs) return word;
    const effectiveEnd =
      nextStart != null
        ? Math.min(
            Math.max(word.endMs, Math.min(nextStart, cappedEnd)),
            nextStart,
          )
        : Math.max(
            word.endMs,
            Math.min(cappedEnd, word.startMs + LAST_WORD_MIN_DURATION_MS),
          );
    return effectiveEnd === word.endMs
      ? word
      : { ...word, endMs: effectiveEnd };
  });

const findNextTimedStart = (
  words: CaptionKaraokeWordTiming[],
  index: number,
): number | null => {
  for (let next = index + 1; next < words.length; next += 1) {
    const start = words[next]?.startMs;
    if (start != null) return start;
  }
  return null;
};

export const getKaraokeHighlightWords = (
  words: CaptionKaraokeWordTiming[],
  currentMs: number,
  effect: CaptionKaraokeHighlightEffect,
  options: KaraokeHighlightTimingOptions = {},
): CaptionKaraokeHighlightWord[] => {
  const effectiveWords = options.preserveWordEndTiming
    ? words
    : withEffectiveWordEnds(words);

  if (effect === 'karaoke-fill') {
    return effectiveWords.map((word) => ({
      ...word,
      fillRatio: getKaraokeWordFillRatio(word, currentMs),
      isActive: isWordActive(word, currentMs),
    }));
  }

  const ranges = buildWordVisualRanges(effectiveWords);
  const sweepRatio = getKaraokeLineSweepRatio(
    effectiveWords,
    currentMs,
    options,
  );

  return effectiveWords.map((word, index) => ({
    ...word,
    fillRatio: getRangeFillRatio(ranges[index], sweepRatio),
    isActive: isWordActive(word, currentMs),
  }));
};

export const getKaraokeLineSweepRatio = (
  rawWords: CaptionKaraokeWordTiming[],
  currentMs: number,
  options: KaraokeHighlightTimingOptions = {},
): number => {
  // withEffectiveWordEnds 는 멱등이라 getKaraokeHighlightWords 를 거쳐 온 입력에도 안전하다.
  const words = options.preserveWordEndTiming
    ? rawWords
    : withEffectiveWordEnds(rawWords);
  const ranges = buildWordVisualRanges(words);
  const anchors = words.flatMap((word, index) => {
    const range = ranges[index];
    if (!range || !hasValidTiming(word)) return [];
    return [{ word, range }];
  });
  const first = anchors[0];
  const last = anchors[anchors.length - 1];
  if (!first || !last) return 0;
  if ((first.word.startMs ?? 0) >= (last.word.endMs ?? 0)) return 0;
  if (currentMs < (first.word.startMs ?? 0)) return 0;
  if (currentMs >= (last.word.endMs ?? 0)) return 1;

  for (let index = 0; index < anchors.length; index += 1) {
    const anchor = anchors[index];
    if (!anchor) continue;
    const startMs = anchor.word.startMs ?? 0;
    const endMs = anchor.word.endMs ?? startMs;
    if (currentMs >= startMs && currentMs <= endMs) {
      const t = smoothstep(
        clampRatio((currentMs - startMs) / (endMs - startMs)),
      );
      return lerp(anchor.range.startRatio, anchor.range.endRatio, t);
    }

    const next = anchors[index + 1];
    if (next && currentMs > endMs && currentMs < (next.word.startMs ?? endMs)) {
      return anchor.range.endRatio;
    }
  }

  return 0;
};

export const getKaraokeWordFillRatio = (
  word: CaptionKaraokeWordTiming,
  currentMs: number,
): number => {
  if (!hasValidTiming(word)) return 0;
  const startMs = word.startMs ?? 0;
  const endMs = word.endMs ?? startMs;
  if (currentMs < startMs) return 0;
  if (currentMs >= endMs) return 1;
  return clampRatio((currentMs - startMs) / (endMs - startMs));
};

const buildWordVisualRanges = (
  words: CaptionKaraokeWordTiming[],
): WordVisualRange[] => {
  const units = words.map((word, index) =>
    Math.max(
      1,
      Array.from(word.text).length + (index < words.length - 1 ? 1 : 0),
    ),
  );
  const totalUnits = Math.max(
    1,
    units.reduce((sum, unit) => sum + unit, 0),
  );
  let cursor = 0;
  return units.map((unit) => {
    const startRatio = cursor / totalUnits;
    cursor += unit;
    return {
      startRatio,
      endRatio: cursor / totalUnits,
    };
  });
};

const getRangeFillRatio = (
  range: WordVisualRange | undefined,
  sweepRatio: number,
): number => {
  if (!range || range.endRatio <= range.startRatio) return 0;
  return clampRatio(
    (sweepRatio - range.startRatio) / (range.endRatio - range.startRatio),
  );
};

const hasValidTiming = (word: CaptionKaraokeWordTiming): boolean =>
  word.startMs != null && word.endMs != null && word.endMs > word.startMs;

const isWordActive = (
  word: CaptionKaraokeWordTiming,
  currentMs: number,
): boolean =>
  hasValidTiming(word) &&
  currentMs >= (word.startMs ?? 0) &&
  currentMs < (word.endMs ?? 0);

const clampRatio = (value: number): number => Math.min(Math.max(value, 0), 1);

const lerp = (from: number, to: number, t: number): number =>
  from + (to - from) * t;

const smoothstep = (value: number): number => value * value * (3 - 2 * value);
